import { useEffect, useMemo, useState } from "react";
import { PartsDTO } from "../../../models/Parts";
import { useSelector } from "react-redux";
import { getPartCategories } from "../../../store/slices/Parts/PartsSelector";
import { CellProps, Column } from "react-table";
import { Box, Button, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle, Divider, FormControl, InputLabel, MenuItem, Select, Stack, TextField, Typography } from "@mui/material";
import { apiStore } from "../../../api/ApiFactory";
import { ReactTable } from "../../../components/table/ReactTable";
import { ClaimPartLineDTO, PartLineDTO } from "../../../models/WarrantyClaimManagement";
import { LineStatus } from "../../../helpers/enums/Warranty";

interface IProps {
    isOpen: boolean;
    laborRate: number;
    onClose: () => void;
    onSelect:(records: PartLineDTO[]) => void;
}

const AddPartLines: React.FC<IProps> = ({isOpen, laborRate, onClose, onSelect}) => {

    const partCategories = useSelector(getPartCategories);
    const [selectedCategory, setSelectedCategory] = useState<string>("");
    const [searchTerm, setSearchTerm] = useState<string>("");

    //using Array instead of SET because we are going to use objects instead to store records and we are going to store them between global filter changes to the table.
    //This would cause objects on a SET to not be equal when new table data is retrieved which is not what we want. 
    const [selectedRows, setSelectedRows] = useState<PartsDTO[]>([]);  

    const [tableData, setTableData] = useState<PartsDTO[] | undefined>();

    const columns: Column<PartsDTO>[] = useMemo(
        () => [
            {
                Header: "Selected",
                Cell: ({row}: CellProps<PartsDTO>) => {

                    const isSelected = selectedRows.some(x => x.partNumber === row.original.partNumber);
                    row.isSelected = isSelected;

                    return (
                        <Checkbox
                            checked={row.isSelected}
                            onClick={() => {
                                toggleRowSelection(row.original);
                                row.toggleRowSelected();
                            }}
                        />
                    );
                },
            },
            {
                Header: "Part Num",
                accessor: "partNumber"
            },
            {
                Header: "Description",
                accessor: "name"
            }
        ],
        [selectedRows]
    );

    const toggleRowSelection = (part: PartsDTO) => {

        let newSelectedRows = selectedRows;

        if (newSelectedRows.some(x => x.partNumber === part.partNumber)) {
            //Delete record
            newSelectedRows = selectedRows.filter(x => x.partNumber !== part.partNumber);
        } else {
            //Add record
            newSelectedRows = [...selectedRows, part];
        }

        setSelectedRows(newSelectedRows);
    };

    useEffect(() => {

        if(isOpen) {

            if(searchTerm === "" && selectedCategory === "") {
                setTableData([]);
            }
            else {
                let params = new URLSearchParams();

                //empty strings will be ignored in filting.
                params.append("searchKeyword", searchTerm);
                params.append("category", selectedCategory);
    
                apiStore.partsApi.getParts(params).then((x) => setTableData(x));
            }


        }
    }, [isOpen, searchTerm, selectedCategory]);

    useEffect(() => {

        if(!isOpen) {
            
            //reset the global filters if there are no selected rows when the control is not open.
            if(selectedRows.length === 0) {
                setSearchTerm("");
                setSelectedCategory("");
                setTableData([]);
            }

        }
    }, [isOpen]);

    const onSave = () => {

        const selectedPartLines = selectedRows.map((item) => 
            {
                let lineitem: PartLineDTO = {
                    lineNumber: 0,
                    lineStatusCode: LineStatus.PENDING,
                    partNumber: item.partNumber,
                    partDescription: item.name,
                    quantity: 1,
                    costPer: item.price,
                    totalCost: (item.laborHours ?? 0) * laborRate, //Calc Labor Cost, not part price. 
                    laborHours: item.laborHours ?? 0,
                    laborRate: laborRate
                }

                return lineitem;
            }
        );

        onSelect(selectedPartLines);
        setSelectedRows([]);
        onClose();
    };

    const onReset = () => {
        setSelectedRows([]);
        setSearchTerm("");
        setSelectedCategory("");
    };

    return (
        <Dialog
            open={isOpen}
            onClose={onClose}
            PaperProps={{
                sx: {
                    height: '100%',
                    right: 0,
                    position: 'fixed',
                    top: 0,
                    margin: 0,
                    maxHeight: '100%',
                },
            }}
        >
            <DialogTitle>
                <Stack direction="row" sx={{justifyContent: "space-between", alignItems: "center"}}>
                    <label>Part List</label>
                    <Typography variant="subtitle1" className="info-header">
                        {selectedRows.length} Parts Selected
                    </Typography>
                </Stack>
                
            </DialogTitle>
            
            <DialogContent dividers>
                <FormControl fullWidth sx={{ mb: 2 }}>
                    <InputLabel id="labor-group-label" shrink={selectedCategory !== ""}>
                        Part Categories
                    </InputLabel>
                    <Select labelId="labor-group-label" value={selectedCategory} onChange={(e:any) => setSelectedCategory(e.target.value)}
                        label="Part Categories"
                    >
                        <MenuItem value="">
                            <em>Part Categories</em>
                        </MenuItem>
                        {partCategories.map((item, index) => (
                            <MenuItem key={index} value={item.value}>
                                {item.name}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <InputLabel>Enter search terms (Part #, Description)</InputLabel>
                <TextField
                    fullWidth
                    size="small"
                    placeholder="Search terms"
                    onBlur={(e) => setSearchTerm(e.target.value)}
                    sx={{marginBottom:"16px"}}
                />

                {tableData !== undefined && (
                    <ReactTable<PartsDTO>
                        name="add-part-list"
                        serverOnChange={false}
                        columns={columns}
                        data={tableData!}
                        isLoading={false}
                        inlineLockState={false}
                    />
                )}

            </DialogContent>
            
            <DialogActions>
                <Button
                    onClick={onReset}
                    color="secondary"
                    sx={{
                        border: '1px solid #B0BEC5',
                        boxShadow: '0px 4px 6px rgba(0, 0, 0, 0.1)',
                        '&:hover': {
                            boxShadow: '0px 6px 10px rgba(0, 0, 0, 0.2)',
                        }
                    }}
                >
                    Reset
                </Button>
                <Button
                    onClick={onClose}
                    color="secondary"
                    sx={{
                        border: '1px solid #B0BEC5',
                        boxShadow: '0px 4px 6px rgba(0, 0, 0, 0.1)',
                        '&:hover': {
                            boxShadow: '0px 6px 10px rgba(0, 0, 0, 0.2)',
                        }
                    }}
                >
                    Close
                </Button>

                <Box ml={2}>
                    <Button
                        onClick={onSave}
                        variant="contained"
                        type="submit"
                        sx={{
                            border: '1px solid black',
                            boxShadow: '0px 4px 6px rgba(0, 0, 0, 0.1)',
                            '&:hover': {
                                boxShadow: '0px 6px 10px rgba(0, 0, 0, 0.2)',
                            },
                            textTransform: 'none'
                        }}
                    >
                        Add Parts
                    </Button>
                </Box>
            </DialogActions>

        </Dialog>
    );

}



export default AddPartLines;
